import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  Clock,
  Eye,
  FileCheck,
  FileOutput,
  Info,
  Search,
  type LucideIcon,
} from 'lucide-react';

import { Card, CardContent } from '@/ui/base/card';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Alert, AlertDescription } from '@/ui/base/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/ui/base/dialog';
import { Separator } from '@/ui/base/separator';
import { ErrorState } from '@/ui/ErrorState';
import { EmptyState } from '@/ui/EmptyState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MonthFilter } from '@/ui/MonthFilter';
import { Skeleton } from '@/ui/base/skeleton';
import { cn } from '@/nucleo/utilitarios';

import { useConciliacaoSof } from '../hooks/useSofExtras';
import type { ClassificacaoConciliacaoSof, ConciliacaoSofItem } from '../types';
import { EstagioSofBadge } from './EstagioSofBadge';
import { SofHeader } from './SofHeader';
import { SofTabsNav } from './SofTabsNav';

const ROTULO_CLASSIFICACAO: Record<ClassificacaoConciliacaoSof, string> = {
  DIVERGENTE: 'Divergente',
  CONCILIADO: 'Conciliado',
  EM_TRANSITO: 'Em trânsito',
};

const TOM_CLASSIFICACAO: Record<ClassificacaoConciliacaoSof, string> = {
  DIVERGENTE: 'border-destructive/40 text-destructive bg-destructive/10',
  CONCILIADO: 'border-success/40 text-success bg-success/10',
  EM_TRANSITO: 'border-warning/40 text-warning bg-warning/10',
};

const FILTROS = ['TODOS', 'DIVERGENTE', 'CONCILIADO', 'EM_TRANSITO'] as const;
type FiltroClassificacao = (typeof FILTROS)[number];

function competenciaAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

function formatarMes(mes: string): string {
  const [ano, mesNum] = mes.split('-').map(Number);
  if (!ano || !mesNum) return mes;
  const texto = new Date(ano, mesNum - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function KpiBox({
  label,
  value,
  hint,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone: 'ok' | 'warn' | 'danger' | 'default';
  icon: LucideIcon;
}) {
  const corDoTom: Record<typeof tone, string> = {
    ok: 'text-success',
    warn: 'text-warning',
    danger: 'text-destructive',
    default: 'text-primary',
  };
  return (
    <Card className="rounded-xl">
      <CardContent className="pt-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="mt-0.5 text-2xl font-bold tabular-nums">{value}</p>
            {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
          </div>
          <Icon className={cn('h-5 w-5 shrink-0', corDoTom[tone])} aria-hidden="true" />
        </div>
      </CardContent>
    </Card>
  );
}

function LinhaDetalhe({
  icon: Icon,
  label,
  value,
  tone = 'muted',
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  tone?: 'muted' | 'ok' | 'warn' | 'danger';
}) {
  const corDoTom: Record<typeof tone, string> = {
    muted: 'text-muted-foreground',
    ok: 'text-success',
    warn: 'text-warning',
    danger: 'text-destructive',
  };
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', corDoTom[tone])} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate font-medium">{value || '—'}</p>
      </div>
    </div>
  );
}

/** FIN-031.4: cruza o valor da medição (lado SOF) contra o valor pago informado. */
export function SofConciliacaoPage() {
  const [searchParams] = useSearchParams();
  const [mes, definirMes] = useState(competenciaAtual());
  // FIN-031.4: "Conciliar" na aba Status navega para cá em vez de abrir o
  // modal in-place do protótipo (SofConciliacaoTab) — para não perder o
  // credenciado que o usuário queria conciliar, a busca chega pela URL.
  const [busca, definirBusca] = useState(() => searchParams.get('busca') ?? '');
  const [filtro, definirFiltro] = useState<FiltroClassificacao>('TODOS');
  const [detalhe, definirDetalhe] = useState<ConciliacaoSofItem | null>(null);
  const { data, isLoading, isError, refetch } = useConciliacaoSof(mes || undefined);

  const itens = useMemo(() => {
    let lista = data ?? [];
    if (busca) {
      const termo = busca.toLowerCase();
      lista = lista.filter((i) => i.credenciadoNome.toLowerCase().includes(termo));
    }
    if (filtro !== 'TODOS') {
      lista = lista.filter((i) => i.classificacao === filtro);
    }
    return lista;
  }, [data, busca, filtro]);

  const contagens = useMemo(() => {
    const base = data ?? [];
    return {
      TODOS: base.length,
      CONCILIADO: base.filter((i) => i.classificacao === 'CONCILIADO').length,
      DIVERGENTE: base.filter((i) => i.classificacao === 'DIVERGENTE').length,
      EM_TRANSITO: base.filter((i) => i.classificacao === 'EM_TRANSITO').length,
    };
  }, [data]);

  const valores = useMemo(() => {
    const base = data ?? [];
    const valorTotal = base.reduce((s, i) => s + parseFloat(i.valorMedicao), 0);
    const valorPago = base.reduce(
      (s, i) => s + (i.valorPago != null ? parseFloat(i.valorPago) : 0),
      0,
    );
    return { valorTotal, valorPago };
  }, [data]);

  const formatarMoeda = (valor: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);

  const divergenciaDetalhe =
    !!detalhe?.valorPago &&
    Math.abs(parseFloat(detalhe.valorMedicao) - parseFloat(detalhe.valorPago)) > 0.01;

  return (
    <div className="space-y-6">
      <SofHeader />

      <SofTabsNav />

      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="text-xs">
          <strong>Protótipo funcional.</strong> O cruzamento usa o valor da medição (lado SOF)
          contra o valor pago informado (lado pago). Quando a API real do SOF estiver disponível, a
          fonte do "pago" será substituída sem alteração desta tela.
        </AlertDescription>
      </Alert>

      <div className="flex flex-wrap items-end gap-3">
        <MonthFilter value={mes} onChange={definirMes} label="Competência" />
        <div className="min-w-[220px] flex-1">
          <label className="text-xs text-muted-foreground" htmlFor="sof-conciliacao-busca">
            Buscar credenciado
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="sof-conciliacao-busca"
              value={busca}
              onChange={(e) => definirBusca(e.target.value)}
              placeholder="Nome do credenciado…"
              className="h-10 pl-9"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTROS.map((chave) => (
            <Button
              key={chave}
              type="button"
              size="sm"
              variant={filtro === chave ? 'default' : 'outline'}
              onClick={() => definirFiltro(chave)}
            >
              {chave === 'TODOS' ? 'Todos' : ROTULO_CLASSIFICACAO[chave]} ({contagens[chave]})
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiBox
          label="Conciliados"
          value={contagens.CONCILIADO}
          hint={`${formatarMoeda(valores.valorPago)} pagos`}
          tone="ok"
          icon={CheckCircle2}
        />
        <KpiBox
          label="Divergentes"
          value={contagens.DIVERGENTE}
          hint="Pagaram, mas valor difere"
          tone="danger"
          icon={AlertTriangle}
        />
        <KpiBox
          label="Em trânsito"
          value={contagens.EM_TRANSITO}
          hint="Ainda não pagas pelo SOF"
          tone="warn"
          icon={Clock}
        />
        <KpiBox
          label="Total no mês"
          value={formatarMoeda(valores.valorTotal)}
          hint={`${contagens.TODOS} medição(ões)`}
          tone="default"
          icon={Banknote}
        />
      </div>

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : itens.length === 0 ? (
        <EmptyState
          variant="search"
          title="Nenhuma medição encontrada"
          description={`Sem registros em ${mes ? formatarMes(mes) : 'todas as competências'} para o filtro selecionado.`}
        />
      ) : (
        <div className="space-y-2">
          {itens.map((item) => {
            const divergente = item.classificacao === 'DIVERGENTE';
            const ok = item.classificacao === 'CONCILIADO';
            const diferenca =
              item.valorPago != null
                ? parseFloat(item.valorMedicao) - parseFloat(item.valorPago)
                : null;
            return (
              <Card key={item.medicaoId} className="rounded-xl">
                <CardContent className="flex flex-wrap items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.credenciadoNome}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <EstagioSofBadge estagio={item.estagioSof} />
                      <Badge
                        variant="outline"
                        className={cn('text-[10px]', TOM_CLASSIFICACAO[item.classificacao])}
                      >
                        {ROTULO_CLASSIFICACAO[item.classificacao]}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Medição</p>
                    <p className="text-sm font-semibold tabular-nums">
                      <MoneyDisplay value={item.valorMedicao} />
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Pago</p>
                    <p
                      className={cn(
                        'text-sm font-semibold tabular-nums',
                        divergente
                          ? 'text-destructive'
                          : ok
                            ? 'text-success'
                            : 'text-muted-foreground',
                      )}
                    >
                      {item.valorPago != null ? <MoneyDisplay value={item.valorPago} /> : '—'}
                    </p>
                  </div>
                  {divergente && diferenca != null && (
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Δ</p>
                      <p className="text-sm font-semibold tabular-nums text-destructive">
                        <MoneyDisplay value={Math.abs(diferenca).toFixed(2)} />
                      </p>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => definirDetalhe(item)}
                  >
                    <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Detalhar
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!detalhe} onOpenChange={(aberto) => !aberto && definirDetalhe(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>Conciliação SOF × Liquidação/Pagamento</DialogTitle>
                <DialogDescription>{detalhe?.credenciadoNome ?? '—'}</DialogDescription>
              </div>
              {detalhe && (
                <Badge variant="outline" className={cn(TOM_CLASSIFICACAO[detalhe.classificacao])}>
                  {ROTULO_CLASSIFICACAO[detalhe.classificacao]}
                </Badge>
              )}
            </div>
          </DialogHeader>

          {detalhe && (
            <>
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-4 rounded-xl border border-border/60 p-4">
                  <div className="flex items-center gap-2">
                    <FileOutput className="h-4 w-4 text-primary" aria-hidden="true" />
                    <h4 className="text-sm font-semibold">Enviado ao SOF</h4>
                  </div>
                  <LinhaDetalhe
                    icon={Banknote}
                    label="Valor da medição"
                    value={<MoneyDisplay value={detalhe.valorMedicao} />}
                  />
                  <LinhaDetalhe
                    icon={FileOutput}
                    label="Estágio SOF"
                    value={<EstagioSofBadge estagio={detalhe.estagioSof} semTooltip />}
                  />
                  <LinhaDetalhe
                    icon={FileCheck}
                    label="Classificação"
                    value={ROTULO_CLASSIFICACAO[detalhe.classificacao]}
                  />
                </div>

                <div className="space-y-4 rounded-xl border border-border/60 p-4">
                  <div className="flex items-center gap-2">
                    <Banknote className="h-4 w-4 text-success" aria-hidden="true" />
                    <h4 className="text-sm font-semibold">Liquidação · Pagamento</h4>
                  </div>
                  <LinhaDetalhe
                    icon={CheckCircle2}
                    label="Valor pago"
                    value={
                      detalhe.valorPago != null ? <MoneyDisplay value={detalhe.valorPago} /> : '—'
                    }
                    tone={
                      divergenciaDetalhe ? 'danger' : detalhe.valorPago != null ? 'ok' : 'muted'
                    }
                  />
                </div>
              </div>

              <Separator className="my-2" />
              <p className="text-xs text-muted-foreground">
                Detalhamento por empenho individual entra quando o vínculo medição↔empenho existir
                nesta base — hoje a conciliação cruza só os totais.
              </p>

              {divergenciaDetalhe && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                  <strong>Divergência detectada:</strong> valor da medição (
                  <MoneyDisplay value={detalhe.valorMedicao} />) não bate com o valor pago (
                  <MoneyDisplay value={detalhe.valorPago ?? '0'} />
                  ). Diferença:{' '}
                  <span className="font-semibold tabular-nums">
                    <MoneyDisplay
                      value={Math.abs(
                        parseFloat(detalhe.valorMedicao) - parseFloat(detalhe.valorPago ?? '0'),
                      ).toFixed(2)}
                    />
                  </span>
                  .
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SofConciliacaoPage;
